import React, { useMemo, useState } from 'react'
import { Container, Typography, Button, Box } from '@material-ui/core'
import { PrismicRichText } from '@prismicio/react'
import { prismicText } from '../Global/CMSText'
import BasicInput from './inputs/BasicInput'
// Unified BasicInput now handles textarea and select too
import CheckboxInput from './inputs/CheckboxInput'
import FileInput, { FileInputHandle } from './inputs/FileInput'
import {
  DynamicFormDocumentFlexible,
  DynamicFormSlices,
  DynamicFormDocumentDataFlexible,
} from '@/interfaces/common/prismicDynamicForm'
import { submitDynamicForm } from '@/redux/services/dynamicForm'

// Type for form field values
type FormFieldValue = string | number | boolean | File | File[] | string[]

type TDynamicFormProps = {
  formData: DynamicFormDocumentFlexible
  orderNumber?: string
  fileUploadConfig?: {
    entityType: string
  }
}

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/

const DynamicForm: React.FC<TDynamicFormProps> = ({
  formData,
  orderNumber,
  fileUploadConfig,
}) => {
  const [formValues, setFormValues] = useState<Record<string, FormFieldValue>>(
    {}
  )
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [isSubmitted, setIsSubmitted] = useState(false)

  const formDataTyped = formData?.data as DynamicFormDocumentDataFlexible
  const fieldsSlices: DynamicFormSlices[] =
    formDataTyped?.body ?? formDataTyped?.form_body ?? []

  const hasFileInputs = useMemo(
    () => fieldsSlices.some((s) => s.slice_type === 'file_input'),
    [fieldsSlices]
  )

  const fileRefs = React.useRef<Record<string, FileInputHandle | null>>({})

  const handleChange = (fieldName: string, value: FormFieldValue) => {
    setFormValues((prev) => ({ ...prev, [fieldName]: value }))
    if (errors[fieldName]) setErrors((prev) => ({ ...prev, [fieldName]: '' }))
  }

  const validateForm = () => {
    const nextErrors: Record<string, string> = {}

    fieldsSlices.forEach((slice) => {
      const fieldName = prismicText(slice.primary.field_name)
      if (!fieldName) return

      const isRequired = Boolean(slice.primary.required)
      const value = formValues[fieldName]

      if (slice.slice_type === 'file_input') {
        const files: File[] = Array.isArray(value)
          ? (value as File[])
          : value instanceof File
          ? [value]
          : []
        if (isRequired && files.length === 0)
          nextErrors[fieldName] = 'This field is required'
        const maxMB = Number(slice.primary.max_size_mb || 0)
        if (maxMB > 0 && files.length) {
          const tooBig = files.some((f) => f.size > maxMB * 1024 * 1024)
          if (tooBig) nextErrors[fieldName] = `Max file size is ${maxMB} MB`
        }
        return
      }

      // Required validation
      if (isRequired) {
        const isEmpty =
          value === undefined ||
          value === null ||
          value === '' ||
          value === false
        if (isEmpty) nextErrors[fieldName] = 'This field is required'
      }

      // Type-specific validation
      if (slice.slice_type === 'email_input' && value) {
        if (!emailRegex.test(String(value))) {
          nextErrors[fieldName] = 'Invalid email address'
        }
      }
    })

    setErrors(nextErrors)
    return Object.keys(nextErrors).length === 0
  }

  // If any checkbox has primary.disable_sybmit_button_on_unchecked true,
  // submission must be disabled until that checkbox is checked.
  const mustAcceptCheckboxFieldNames: string[] = useMemo(() => {
    return fieldsSlices
      .filter(
        (slice) =>
          slice.slice_type === 'checkbox_input' &&
          Boolean(
            slice.primary.disable_sybmit_button_on_unchecked ||
              slice.primary.disable_submit_button_on_unchecked
          )
      )
      .map((slice) => prismicText(slice.primary.field_name))
      .filter((name): name is string => Boolean(name))
  }, [fieldsSlices])

  const isSubmitDisabledByCheckbox = useMemo(() => {
    if (!mustAcceptCheckboxFieldNames.length) return false
    // Disabled if any gating checkbox is not truthy
    return mustAcceptCheckboxFieldNames.some(
      (fieldName) => !Boolean(formValues[fieldName])
    )
  }, [mustAcceptCheckboxFieldNames, formValues])

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!validateForm()) return

    const uploadedByField: Record<string, string[]> = {}
    try {
      // Upload files via input refs (signed URL flow) just before submit
      const payloadFields: Record<string, FormFieldValue> = { ...formValues }
      if (hasFileInputs && fileUploadConfig) {
        for (const slice of fieldsSlices) {
          if (slice.slice_type !== 'file_input') continue
          const fieldName = prismicText(slice.primary.field_name)
          if (!fieldName) continue
          const handle = fileRefs.current[fieldName]
          if (!handle) continue
          const { uploadedIds } = await handle.upload()
          if (uploadedIds?.length) {
            uploadedByField[fieldName] = uploadedIds
            payloadFields[fieldName] = Array.isArray(formValues[fieldName])
              ? uploadedIds
              : uploadedIds[0]
          }
        }
      }

      await submitDynamicForm({
        oms_order_id: orderNumber || '',
        type: formData?.uid || '',
        data: payloadFields,
      })
      setIsSubmitted(true)
      setFormValues({})
    } catch (err) {
      // Rollback uploaded files if submit failed
      if (fileUploadConfig) {
        const rollbacks: Promise<void>[] = []
        for (const [fieldName, ids] of Object.entries(uploadedByField)) {
          const handle = fileRefs.current[fieldName]
          if (handle && ids?.length) {
            rollbacks.push(handle.rollback(ids))
          }
        }
        await Promise.all(rollbacks)
      }
      // Keep minimal handling consistent with codebase
      alert('Error submitting form. Please try again later.')
    }
  }

  const renderField = (slice: DynamicFormSlices, key: string | number) => {
    const sliceType = slice.slice_type
    const fieldName = prismicText(slice.primary.field_name)
    if (!fieldName) return null
    const fieldLabel = prismicText(slice.primary.field_label)
    const required = Boolean(slice.primary.required)
    const helperText = prismicText(slice.primary.helper_text)
    const errorMsg = errors[fieldName]
    const basicTypes = {
      text_input: 'text',
      email_input: 'email',
      phone_input: 'tel',
      number_input: 'number',
      textarea_input: 'textarea',
      select_input: 'select',
    } as const

    switch (sliceType) {
      case 'text_input':
      case 'email_input':
      case 'phone_input':
      case 'number_input':
      case 'textarea_input':
      case 'select_input': {
        const kind = basicTypes[sliceType]
        const placeholder =
          'placeholder' in slice.primary
            ? prismicText(slice.primary.placeholder)
            : undefined
        const rows = 'rows' in slice.primary ? slice.primary.rows : undefined
        const options =
          kind === 'select' && 'items' in slice
            ? slice.items.map((item) => ({
                label: prismicText(item.option_label),
                value: prismicText(item.option_value),
              }))
            : []

        const fieldValue = formValues[fieldName]
        const inputValue: string | number | undefined =
          typeof fieldValue === 'string' || typeof fieldValue === 'number'
            ? fieldValue
            : undefined

        return (
          <BasicInput
            key={key}
            label={fieldLabel}
            name={fieldName}
            kind={kind}
            required={required}
            placeholder={placeholder}
            value={inputValue}
            error={errorMsg}
            helperText={helperText}
            onChange={(val) => handleChange(fieldName, val)}
            rows={rows}
            options={options}
          />
        )
      }

      case 'checkbox_input':
        return (
          <CheckboxInput
            key={key}
            labelRich={slice.primary.field_label}
            name={fieldName}
            checked={Boolean(formValues[fieldName])}
            error={errorMsg}
            helperText={helperText}
            onChange={(val) => handleChange(fieldName, val)}
          />
        )

      case 'file_input': {
        const fileValue = formValues[fieldName]
        const typedFileValue =
          fileValue instanceof File
            ? fileValue
            : Array.isArray(fileValue)
            ? (fileValue as File[])
            : null

        return (
          <FileInput
            key={key}
            ref={(el) => (fileRefs.current[fieldName] = el)}
            label={fieldLabel}
            name={fieldName}
            required={required}
            helperText={helperText}
            accept={prismicText(slice.primary.accept) || undefined}
            multiple={Boolean(slice.primary.multiple)}
            maxSizeMB={slice.primary.max_size_mb}
            value={typedFileValue}
            onChange={(val) => handleChange(fieldName, val)}
            config={
              fileUploadConfig
                ? {
                    entityType: fileUploadConfig.entityType,
                  }
                : undefined
            }
          />
        )
      }

      default:
        return null
    }
  }

  if (isSubmitted) {
    return (
      <Container maxWidth="md">
        <Box py={8} textAlign="center">
          <PrismicRichText field={formData?.data?.success_message} />
        </Box>
      </Container>
    )
  }

  return (
    <Container maxWidth="md">
      <Box py={4}>
        <Typography variant="h1" gutterBottom>
          {prismicText(formData?.data?.form_title)}
        </Typography>

        {formData?.data?.form_description && (
          <Box mb={3}>
            <PrismicRichText field={formData?.data?.form_description} />
          </Box>
        )}

        <form onSubmit={onSubmit}>
          {fieldsSlices.map((slice, index) => renderField(slice, index))}

          <Box mt={4}>
            <Button
              type="submit"
              variant="contained"
              color="primary"
              size="large"
              disabled={isSubmitDisabledByCheckbox}
            >
              {prismicText(formData?.data?.submit_button_text, 'Submit')}
            </Button>
          </Box>
        </form>
      </Box>
    </Container>
  )
}

export default DynamicForm
